import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { ArrowLeft, Save, Monitor, Cpu, Settings, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import ThemeToggle from '../../../components/ThemeToggle';

const labelClassName = 'text-sm font-medium text-foreground';
const fieldClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50';
const selectTriggerClassName =
  'h-11 rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm focus:ring-2 focus:ring-primary/25 focus:border-primary/50';

export default function AddDeviceTypes() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { post } = useApi();
  
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      model_name: '',
      manufacturer: '',
      firmware_version: '',
      hardware_version: '',
      description: '',
      status: 'active',
      price: '',
      created_ip: '',
      created_by: ''
    }
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      console.log('Device type creation payload:', data);
      
      const response = await post('/device-types', data);

      console.log('Device type creation response:', response);
      
      toast.success('Device type added successfully!');
      navigate('/admin/device-types');
    } catch (error) {
      console.error('Error creating device type:', error);
      
      let errorMessage = 'An error occurred while creating the device type. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl bg-background text-foreground min-h-screen">
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Add New Device Type</h1>
          <p className="text-muted-foreground">Create a new device type for TrackYourWheelz</p>
        </div>
  
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Monitor className="w-5 h-5" />
              Basic Information
            </CardTitle>
            <CardDescription>
              Basic device type details and identification
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className={labelClassName}>Device Name *</Label>
                <Input
                  id="name"
                  {...register('name', { required: 'Device name is required' })}
                  placeholder="Enter device name (e.g., AIS-145 GPS)"
                  className={fieldClassName}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="model_name" className={labelClassName}>Model Name *</Label>
                <Input
                  id="model_name"
                  {...register('model_name', { required: 'Model name is required' })}
                  placeholder="Enter model name (e.g., AIS145-M1)"
                  className={fieldClassName}
                />
                {errors.model_name && (
                  <p className="text-sm text-red-500">{errors.model_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="price" className={labelClassName}>Price</Label>
                <Input
                  id="price"
                  {...register('price', { required: 'Price is required' })}
                  placeholder="Enter price (e.g., 1000)"
                  className={fieldClassName}
                />
                {errors.price && (
                  <p className="text-sm text-red-500">{errors.price.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="manufacturer" className={labelClassName}>Manufacturer *</Label>
                <Input
                  id="manufacturer"
                  {...register('manufacturer', { required: 'Manufacturer is required' })}
                  placeholder="Enter manufacturer (e.g., Teltonika)"
                  className={fieldClassName}
                />
                {errors.manufacturer && (
                  <p className="text-sm text-red-500">{errors.manufacturer.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className={labelClassName}>Status *</Label>
                <Select onValueChange={(value) => setValue('status', value)}>
                  <SelectTrigger className={selectTriggerClassName}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-500">{errors.status.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Specifications */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Cpu className="w-5 h-5" />
              Technical Specifications
            </CardTitle>
            <CardDescription>
              Hardware and firmware version information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firmware_version" className={labelClassName}>Firmware Version *</Label>
                <Input
                  id="firmware_version"
                  {...register('firmware_version', { required: 'Firmware version is required' })}
                  placeholder="Enter firmware version (e.g., 1.2.3)"
                  className={fieldClassName}
                />
                {errors.firmware_version && (
                  <p className="text-sm text-red-500">{errors.firmware_version.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="hardware_version" className={labelClassName}>Hardware Version *</Label>
                <Input
                  id="hardware_version"
                  {...register('hardware_version', { required: 'Hardware version is required' })}
                  placeholder="Enter hardware version (e.g., HW-2025)"
                  className={fieldClassName}
                />
                {errors.hardware_version && (
                  <p className="text-sm text-red-500">{errors.hardware_version.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card className="border-1 border-muted-foreground/20 bg-card shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-5 h-5" />
              Additional Information
            </CardTitle>
            <CardDescription>
              Description and tracking information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="description" className={labelClassName}>Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Enter device description (e.g., Government approved AIS-140 GPS tracker)"
                className={`min-h-[100px] rounded-lg border-1 border-muted-foreground/20 bg-background text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:border-primary/50`}
                rows={3}
              />
            </div>

            
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
          >
            Cancel
          </Button>
          <Button type="submit" variant="outline" disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-foreground mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Device Type
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}